import React, { PropsWithChildren, useEffect, useMemo, useState } from 'react';

import { useGetCurrentUserDetails } from '@/api/util/useGetCurrentUserDetails.util.ts';
import { FullScreenLoader } from '@/components/fullScreenLoader/FullScreenLoader.comp.tsx';
import { useGetUserTours } from '@/components/tour/util/useGetUserTours.util.ts';
import {
  UserContext,
  UserContextProps,
  UserDefaultValues,
} from '@/context/userContext/User.context.ts';
import { PATHS_WITHOUT_SESSION } from '@/routes/RoutesNotRequiringAuth.util.ts';

export const UserContextProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const [user, setUser] = useState<UserContextProps | null>(null);
  const { isError, userDetails } = useGetCurrentUserDetails({
    pathWithoutSession: PATHS_WITHOUT_SESSION,
  });
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    if (PATHS_WITHOUT_SESSION) {
      setUser(UserDefaultValues);
      setIsInitialized(true);
      return;
    }
    if (!userDetails) {
      return;
    }
    setUser({
      ...userDetails,
      userSelectOption: {
        label: userDetails.lastName + ' ' + userDetails?.firstName,
        value: userDetails.userId,
      },
    });
    setIsInitialized(true);
  }, [userDetails]);

  if (!isInitialized) {
    return <FullScreenLoader />;
  }

  if (isError || (isInitialized && !user)) {
    return <div>Error</div>;
  }

  if (PATHS_WITHOUT_SESSION || !user) {
    return <>{children}</>;
  }

  return <UserContextProviderInner user={user}>{children}</UserContextProviderInner>;
};

const UserContextProviderInner: React.FC<PropsWithChildren & { user: UserContextProps }> = ({
  children,
  user,
}) => {
  const userContext = useMemo<UserContextProps>(() => user, [user]);

  return (
    <UserContext value={userContext}>
      <UserDataProvider>{children}</UserDataProvider>
    </UserContext>
  );
};

const UserDataProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const { isGetting } = useGetUserTours();

  if (isGetting) {
    return <FullScreenLoader />;
  }

  return <>{children}</>;
};
